"use client";

import { m, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import SectionHeader from "../sectionHeader";

interface ShowcaseItem {
  image?: { asset: { url: string }; alt?: string };
  title?: string;
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    items?: ShowcaseItem[];
    layout?: "grid" | "alternating" | "marquee";
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

function ShowcaseImage({ item }: { item: ShowcaseItem }) {
  if (!item.image) return <div className="bg-surface w-full h-full" />;
  const alt = item.image.alt ?? item.title ?? "TMRW ad creative example";
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={item.image.asset.url}
      alt={alt}
      className="object-contain w-full h-full"
      loading="eager"
      decoding="async"
    />
  );
}

export default function ShowcaseSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <m.section
      ref={ref}
      className="section"
      transition={{ staggerChildren: 0.05, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
    >
      <div className="container mx-auto flex flex-col gap-12">
        <SectionHeader title={data?.title} headline={data?.headline} />
      </div>

      {data?.layout === "marquee" && data?.items ? (
        <MarqueeShowcase items={data.items} />
      ) : data?.layout === "alternating" ? (
        <div className="container mx-auto flex flex-col gap-4">
          {/* Row 1: square left + 2 rectangles right */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <m.div variants={itemVariants} className="bg-surface aspect-square" />
            <div className="flex flex-col gap-4">
              <m.div variants={itemVariants} className="bg-surface aspect-square md:flex-1 md:aspect-auto" />
              <m.div variants={itemVariants} className="bg-surface aspect-square md:flex-1 md:aspect-auto" />
            </div>
          </div>
          {/* Row 2: 2 rectangles left + square right */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-4">
              <m.div variants={itemVariants} className="bg-surface aspect-square md:flex-1 md:aspect-auto" />
              <m.div variants={itemVariants} className="bg-surface aspect-square md:flex-1 md:aspect-auto" />
            </div>
            <m.div variants={itemVariants} className="bg-surface aspect-square" />
          </div>
        </div>
      ) : (
        <div className="container mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.items?.map((item, index) => (
            <m.div
              key={index}
              variants={itemVariants}
              className="bg-surface aspect-[4/3]"
            />
          ))}
        </div>
      )}
    </m.section>
  );
}

function MarqueeShowcase({ items }: { items: ShowcaseItem[] }) {
  const mid = Math.ceil(items.length / 2);
  const row1 = items.slice(0, mid);
  const row2 = items.slice(mid);

  return (
    <div className="flex flex-col gap-4 mt-12 md:overflow-hidden">
      {/* Mobile: embla with AutoScroll — slow right→left marquee, pauses on touch, infinite loop */}
      <div className="md:hidden">
        <MobileAutoScrollRow items={items} />
      </div>

      {/* Desktop: two rows */}
      <div className="hidden md:flex flex-col gap-4">
        <div className="overflow-hidden">
          <div className="flex gap-4 marquee-scroll-slow" style={{ width: "max-content" }}>
            {[...row1, ...row1, ...row1, ...row1].map((item, index) => (
              <div key={index} className="w-[220px] lg:w-[260px] shrink-0 rounded overflow-hidden aspect-[4/5] bg-white">
                <ShowcaseImage item={item} />
              </div>
            ))}
          </div>
        </div>

        <div className="overflow-hidden">
          <div className="flex gap-4 marquee-scroll-reverse-slow" style={{ width: "max-content" }}>
            {[...row2, ...row2, ...row2, ...row2].map((item, index) => (
              <div key={index} className="w-[220px] lg:w-[260px] shrink-0 rounded overflow-hidden aspect-[4/5] bg-white">
                <ShowcaseImage item={item} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileAutoScrollRow({ items }: { items: ShowcaseItem[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const duplicated = [...items, ...items];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || paused) return;

    let rafId: number;
    const tick = () => {
      const halfWidth = el.scrollWidth / 2;
      if (el.scrollLeft >= halfWidth) {
        el.scrollLeft -= halfWidth;
      } else {
        el.scrollLeft += 0.5;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [paused]);

  const pause = () => setPaused(true);
  const resume = () => setPaused(false);

  return (
    <div
      ref={scrollRef}
      className="overflow-x-auto"
      onTouchStart={pause}
      onTouchEnd={resume}
      onMouseDown={pause}
      onMouseUp={resume}
      onMouseLeave={resume}
    >
      <div className="flex gap-4 w-max">
        {duplicated.map((item, index) => (
          <div key={index} className="w-[200px] shrink-0 rounded overflow-hidden aspect-[4/5] bg-white">
            <ShowcaseImage item={item} />
          </div>
        ))}
      </div>
    </div>
  );
}
